// The offline copy's database on the device (IndexedDB through Dexie), one per server. What is in it:
//   rows       a message's headers, attachment list and snippet: GET /api/message without the body. Small; kept for
//              every message of an offline folder (a listing never shows a message without one).
//   bodies     the message's sanitized HTML and text.
//   texts      the folded text the offline search looks in (subject, people, attachment names, body), apart from
//              the bodies so a search never reads HTML.
//   files      attachments (of the folders kept with attachments), as ArrayBuffers: Blobs in IndexedDB have been
//              unreliable in WebKit.
//   locations  which offline folder holds which message, with its flags there; a folder's listing, newest first.
//   folders    per offline folder: the change marker of the listing it was last compared with, and whether every
//              message of that listing is stored.
//   meta       the last /api/folders answer, /api/me and /api/settings, so the app starts without a connection.
//   outbox     mail and actions (read, star, move, trash) waiting for the server, in the order they were made
//              (outbox.ts).
//   outfiles   the files from the device of the mail in the outbox (ArrayBuffers, as in files).
//   turns      actions (read, star, move, trash, a folder marked read) taken while the server answers, waiting for
//              their turn (outbox.perform): kept until they are done, so one the app was closed before it came to is
//              done when the app starts again.
//   draftfiles the files from the device added to the drafts (outgoing.ts: the messages being written or left unsent),
//              by the id a draft lists them under, so they are still attached when the app is closed and opened again
//              (ArrayBuffers, as in files).
// Bodies, texts and files are what grows: when storage runs out the oldest go first (makeRoom).

import Dexie, { type Table } from "dexie";
import type { Message, SendRequest } from "./api";
import { API_BASE } from "./auth";
import type { Draft } from "./outgoing";
import type { ComposeMode } from "./route";

/** A message without its body: GET /api/message less html and body_text, plus the listing's snippet. */
export interface CachedRow extends Omit<Message, "html" | "body_text"> {
  snippet: string;
}

export interface CachedBody {
  id: string;
  /** The message's date as an index key: "" when it has none (IndexedDB keys cannot be null). */
  date_key: string;
  html: string | null;
  body_text: string | null;
}

export interface CachedText {
  id: string;
  date_key: string;
  /** Subject, people, attachment names and body text, folded (lower case, no accents). */
  text: string;
}

export interface CachedFile {
  id: string;
  part: number;
  date_key: string;
  mime: string;
  data: ArrayBuffer;
}

export interface CachedLocation {
  folder: string;
  id: string;
  date_key: string;
  flags: string[];
}

export interface FolderState {
  name: string;
  /** The folder's change marker when it was last listed. */
  change: number;
  /** Every message of that listing is stored, with its attachments when they are wanted. */
  complete: boolean;
  attachments: boolean;
  messages: number;
  synced_at: string | null;
}

/** An action on a message waiting in the outbox. `from`: the folder it was in, which the server checks first. */
export type SingleAction =
  | { kind: "read"; message: string; read: boolean }
  | { kind: "flag"; message: string; flagged: boolean }
  | { kind: "move"; message: string; from: string; folder: string }
  | { kind: "trash"; message: string; from: string };

/** The same on several messages at once (those chosen in a list), done by one request (bulk.ts): the server acts on
 * them in one IMAP session and answers for each. `from`: the folder they were listed in, checked for each. */
export type BulkAction =
  | { kind: "read-many"; messages: string[]; read: boolean }
  | { kind: "flag-many"; messages: string[]; flagged: boolean }
  | { kind: "move-many"; messages: string[]; from: string; folder: string }
  | { kind: "trash-many"; messages: string[]; from: string };

export type OutboxAction = SingleAction | BulkAction;

/** The messages an action is on. */
export function messagesOf(a: OutboxAction): string[] {
  return "messages" in a ? a.messages : [a.message];
}

/** The action on one of the messages of an action on several (the same action on one message); a single action as it is. */
export function singleOn(a: OutboxAction, message: string): SingleAction {
  switch (a.kind) {
    case "read-many":
      return { kind: "read", message, read: a.read };
    case "flag-many":
      return { kind: "flag", message, flagged: a.flagged };
    case "move-many":
      return { kind: "move", message, from: a.from, folder: a.folder };
    case "trash-many":
      return { kind: "trash", message, from: a.from };
    default:
      return a;
  }
}

/** Mail waiting in the outbox: the request as it is sent (with the archived attachments; the files from the device
 * are in outfiles under its idempotency key) and the draft it came from, to edit it again. */
export interface OutboxMail {
  kind: "mail";
  request: SendRequest;
  draft: Draft;
  mode: ComposeMode;
  source: string | null;
  files: { name: string; mime: string; size: number }[];
}

/** What an action changed in the copy, to put back when it is cancelled. */
export interface CopyUndo {
  /** The message's locations before it. */
  locations: CachedLocation[];
  /** The row's folders, flags, unread and flagged before it (null: the copy has no row). */
  row: Pick<CachedRow, "folders" | "flags" | "unread" | "flagged"> | null;
}

export type OutboxEntry = (OutboxAction | OutboxMail) & {
  /** The order things were done in. */
  seq?: number;
  /** The subject of the message acted on, or of the mail. */
  subject: string | null;
  created_at: string;
  /** Attempts the server answered with a failure. */
  attempts: number;
  error: string | null;
  /** The server refused the mail: it waits to be edited or deleted. */
  refused: boolean;
  /** After a failure the server may get over: not tried again before this time (ms). */
  next_at: number;
  /** What a single action changed in the copy. */
  undo?: CopyUndo;
  /** What an action on several messages changed in the copy, by message id. */
  undos?: Record<string, CopyUndo>;
};

/** A whole folder marked read up to the newest mail it held when the app listed it (`upto`, from /api/folders). */
export interface FolderRead {
  kind: "folder-read";
  folder: string;
  upto: string;
}

/** An action waiting for its turn (outbox.perform, outbox.markFolderRead), kept until it is done. `session`: the page
 * that took it, which does it; another page takes it over only once that one is gone (outbox.resumeTurns). */
export interface TurnEntry {
  seq?: number;
  session: string;
  action: OutboxAction | FolderRead;
  subject: string | null;
  created_at: string;
}

export interface OutboxFile {
  key: string;
  n: number;
  name: string;
  mime: string;
  data: ArrayBuffer;
}

/** A file from the device added to a draft (outgoing.ts), under the id the draft lists it by. */
export interface DraftFileData {
  id: string;
  name: string;
  mime: string;
  data: ArrayBuffer;
}

export class OfflineDb extends Dexie {
  rows!: Table<CachedRow, string>;
  bodies!: Table<CachedBody, string>;
  texts!: Table<CachedText, string>;
  files!: Table<CachedFile, [string, number]>;
  locations!: Table<CachedLocation, [string, string]>;
  folders!: Table<FolderState, string>;
  meta!: Table<{ key: string; value: unknown }, string>;
  outbox!: Table<OutboxEntry, number>;
  outfiles!: Table<OutboxFile, [string, number]>;
  turns!: Table<TurnEntry, number>;
  draftfiles!: Table<DraftFileData, string>;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      rows: "id",
      bodies: "id, date_key",
      texts: "id, date_key",
      files: "[id+part], id, date_key",
      locations: "[folder+id], [folder+date_key], id",
      folders: "name",
      meta: "key",
    });
    this.version(2).stores({ outbox: "++seq", outfiles: "[key+n], key" });
    this.version(3).stores({ turns: "++seq" });
    this.version(4).stores({ draftfiles: "id" });
  }
}

const DB_NAME = `ym-offline:${API_BASE}`;
let instance: OfflineDb | null = null;

export function db(): OfflineDb {
  instance ??= new OfflineDb(DB_NAME);
  return instance;
}

/** Sign-out: the offline copy leaves the device with the tokens. */
export async function deleteOfflineCopy(): Promise<void> {
  const old = instance;
  instance = null;
  if (old) await old.delete();
  else await Dexie.delete(DB_NAME);
}

let generation = 0;

/** The copy changed outside a sync (an action shown in it at once): a search kept in memory is done again. */
export function copyChanged(): void {
  generation++;
}

export function copyGeneration(): number {
  return generation;
}

/** The messages an action in the outbox is waiting for: the sync leaves them as the action left them in the copy. */
export async function heldMessages(): Promise<Set<string>> {
  return new Set((await db().outbox.toArray()).flatMap((e) => (e.kind === "mail" ? [] : messagesOf(e))));
}

export function dateKey(date: string | null): string {
  return date ?? "";
}

export async function getMeta<T>(key: string): Promise<T | null> {
  return ((await db().meta.get(key))?.value as T | undefined) ?? null;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db().meta.put({ key, value });
}

/** The listing of an offline folder, newest first (messages without a date last, as on the server). */
export function folderLocations(name: string) {
  return db().locations.where("[folder+date_key]").between([name, Dexie.minKey], [name, Dexie.maxKey]);
}

export function isQuotaError(e: unknown): boolean {
  const err = e as { name?: string; inner?: { name?: string } } | null;
  return err?.name === "QuotaExceededError" || err?.inner?.name === "QuotaExceededError";
}

/** Storage ran out and nothing older than the message being stored was left to make room with. */
export class StorageFull extends Error {
  constructor(readonly before: string) {
    super("The device's storage for the app is full");
    this.name = "StorageFull";
  }
}

/** How many attachments, bodies or texts one round of makeRoom deletes. */
const EVICT_FILES = 25;
const EVICT_BODIES = 100;

/** Deletes the oldest attachments older than `before` (a date key); when there are none the oldest bodies, and last
 * the oldest search texts (the message can be found but not read). Returns the date key of the newest one deleted,
 * or null when nothing was older. */
export async function makeRoom(before: string): Promise<string | null> {
  const d = db();
  const order = [[d.files, EVICT_FILES], [d.bodies, EVICT_BODIES], [d.texts, EVICT_BODIES]] as const;
  for (const [table, count] of order) {
    // the index keys only: the values (attachments especially) are never read into memory
    const dates = (await table.where("date_key").below(before).limit(count).keys()) as string[];
    if (dates.length === 0) continue;
    await table.where("date_key").below(before).limit(count).delete();
    return dates[dates.length - 1];
  }
  return null;
}

/** Runs a write, making room by deleting older bodies and attachments while storage is full; StorageFull when there
 * is nothing older left. `evicted` hears the date key of the newest message whose body or attachment went. */
export async function withRoom<T>(date: string, write: () => Promise<T>, evicted: (key: string) => void = () => {}): Promise<T> {
  for (;;) {
    try {
      return await write();
    } catch (e) {
      if (!isQuotaError(e)) throw e;
      const freed = await makeRoom(date);
      if (freed === null) throw new StorageFull(date);
      evicted(freed);
    }
  }
}
